import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Trash2, Highlighter } from 'lucide-react';
import { rateChartAPI } from '../services/api';

// JOB CARD PRESSING Form - Exact replica of paper form

const JobCardPressingForm = forwardRef(({ initialData, onChange }, ref) => {
  const [formData, setFormData] = useState({
    job_card_number: '',
    customer: '',
    date: new Date().toISOString().split('T')[0],
    reference: '',
    mob_no: '',
    delivery_date: '',
    ref_job_card_no: '',
    work_in_charge: '',
    // NORMAL PRESSING DETAILS section
    hot_pressing_lines: [],
    // PVC & ACRYLIC PRESSING section
    veneer_pressing_lines: [],
    ...initialData
  });

  const [normalPressingItems, setNormalPressingItems] = useState([]);
  const [pvcPressingItems, setPvcPressingItems] = useState([]);

  useEffect(() => {
    const loadPressingItems = async () => {
      try {
        const [normalItems, pvcItems] = await Promise.all([
          rateChartAPI.getAll(false, 'normal_pressing'),
          rateChartAPI.getAll(false, 'pvc_pressing'),
        ]);
        setNormalPressingItems(normalItems.map(i => i.description));
        setPvcPressingItems(pvcItems.map(i => i.description));
      } catch (err) {
        console.error('Failed to load pressing rate chart items:', err);
      }
    };
    loadPressingItems();
  }, []);

  useImperativeHandle(ref, () => ({
    getData: () => formData,
    validate: () => {
      return formData.customer && formData.work_in_charge;
    }
  }));

  useEffect(() => {
    if (onChange) onChange(formData);
  }, [formData, onChange]);

  const addHotPressingLine = () => {
    setFormData(prev => ({
      ...prev,
      hot_pressing_lines: [...prev.hot_pressing_lines, {
        sl_no: prev.hot_pressing_lines.length + 1,
        description: '',
        ply_qty: '',
        side: '2',
        total: '',
        details: ''
      }]
    }));
  };

  const addVeneerPressingLine = () => {
    setFormData(prev => ({
      ...prev,
      veneer_pressing_lines: [...prev.veneer_pressing_lines, {
        sl_no: prev.veneer_pressing_lines.length + 1,
        description: '',
        ply_qty: '',
        side: '2',
        total: '',
        details: ''
      }]
    }));
  };

  const updateHotPressingLine = (index, field, value) => {
    setFormData(prev => {
      const lines = [...prev.hot_pressing_lines];
      lines[index] = { ...lines[index], [field]: value };
      if (field === 'ply_qty' || field === 'side') {
        const plyQty = field === 'ply_qty' ? parseInt(value) || 0 : parseInt(lines[index].ply_qty) || 0;
        const side = field === 'side' ? parseInt(value) || 1 : parseInt(lines[index].side) || 1;
        lines[index].total = (plyQty * side).toString();
      }
      return { ...prev, hot_pressing_lines: lines };
    });
  };

  const updateVeneerPressingLine = (index, field, value) => {
    setFormData(prev => {
      const lines = [...prev.veneer_pressing_lines];
      lines[index] = { ...lines[index], [field]: value };
      if (field === 'ply_qty' || field === 'side') {
        const plyQty = field === 'ply_qty' ? parseInt(value) || 0 : parseInt(lines[index].ply_qty) || 0;
        const side = field === 'side' ? parseInt(value) || 1 : parseInt(lines[index].side) || 1;
        lines[index].total = (plyQty * side).toString();
      }
      return { ...prev, veneer_pressing_lines: lines };
    });
  };

  const removeHotPressingLine = (index) => {
    setFormData(prev => ({
      ...prev,
      hot_pressing_lines: prev.hot_pressing_lines.filter((_, i) => i !== index)
    }));
  };

  const removeVeneerPressingLine = (index) => {
    setFormData(prev => ({
      ...prev,
      veneer_pressing_lines: prev.veneer_pressing_lines.filter((_, i) => i !== index)
    }));
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="bg-white border-2 border-gray-800 rounded-none font-mono text-sm">
      {/* Datalists for suggestions */}
      <datalist id="normal-pressing-descriptions">
        {normalPressingItems.map(desc => (
          <option key={desc} value={desc} />
        ))}
      </datalist>
      <datalist id="pvc-pressing-descriptions">
        {pvcPressingItems.map(desc => (
          <option key={desc} value={desc} />
        ))}
      </datalist>

      {/* Header Section - Job Card Details */}
      <div className="border-b-2 border-gray-800 p-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <label className="font-bold whitespace-nowrap text-xs">JOB CARD NO:</label>
            <Input value={formData.job_card_number} readOnly className="border-0 border-b-2 border-gray-800 rounded-none h-7 text-sm bg-gray-50" />
          </div>
          <div className="flex items-center gap-2">
            <label className="font-bold whitespace-nowrap text-xs">DATE:</label>
            <Input type="date" value={formData.date} onChange={(e) => updateField('date', e.target.value)} className="border-0 border-b-2 border-gray-800 rounded-none h-7 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="font-bold whitespace-nowrap text-xs">CUSTOMER:</label>
            <Input value={formData.customer} readOnly className="border-0 border-b-2 border-gray-800 rounded-none h-7 text-sm bg-gray-50" />
          </div>
          <div className="flex items-center gap-2">
            <label className="font-bold whitespace-nowrap text-xs">DELIVERY DATE:</label>
            <Input type="date" value={formData.delivery_date} onChange={(e) => updateField('delivery_date', e.target.value)} className="border-0 border-b-2 border-gray-800 rounded-none h-7 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="font-bold whitespace-nowrap text-xs">REFERENCE:</label>
            <Input value={formData.reference} onChange={(e) => updateField('reference', e.target.value)} className="border-0 border-b-2 border-gray-800 rounded-none h-7 text-sm" placeholder="e.g., 1937-TRA" />
          </div>
          <div className="flex items-center gap-2">
            <label className="font-bold whitespace-nowrap text-xs">MOB. NO:</label>
            <Input value={formData.mob_no} onChange={(e) => updateField('mob_no', e.target.value)} className="border-0 border-b-2 border-gray-800 rounded-none h-7 text-sm" placeholder="Mobile number" />
          </div>
          <div className="flex items-center gap-2">
            <label className="font-bold whitespace-nowrap text-xs">REF J.C NO:</label>
            <Input value={formData.ref_job_card_no} onChange={(e) => updateField('ref_job_card_no', e.target.value)} className="border-0 border-b-2 border-gray-800 rounded-none h-7 text-sm" placeholder="Reference job card" />
          </div>
          <div className="flex items-center gap-2">
            <label className="font-bold whitespace-nowrap text-xs">WORK IN-CHARGE:</label>
            <Input value={formData.work_in_charge} onChange={(e) => updateField('work_in_charge', e.target.value)} className="border-0 border-b-2 border-gray-800 rounded-none h-7 text-sm" placeholder="Name" />
          </div>
        </div>
      </div>

      {/* NORMAL PRESSING DETAILS Section */}
      <div className="border-b-2 border-gray-800">
        <div className="bg-blue-100 p-2 font-bold text-center border-b border-gray-800 flex justify-between items-center">
          <span>NORMAL PRESSING DETAILS</span>
          <Button size="sm" variant="outline" onClick={addHotPressingLine} className="h-7" data-testid="add-normal-pressing-row">
            <Plus className="w-4 h-4 mr-1" /> Add Row
          </Button>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-400 p-2 w-16">SL. NO.</th>
              <th className="border border-gray-400 p-2">NORMAL PRESSING</th>
              <th className="border border-gray-400 p-2 w-20">PLY QTY</th>
              <th className="border border-gray-400 p-2 w-16">SIDE</th>
              <th className="border border-gray-400 p-2 w-16">TOTAL</th>
              <th className="border border-gray-400 p-2">DETAILS</th>
              <th className="border border-gray-400 p-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {formData.hot_pressing_lines.length === 0 ? (
              <tr>
                <td colSpan="7" className="border border-gray-400 p-4 text-center text-gray-500">
                  Click "Add Row" to add pressing details
                </td>
              </tr>
            ) : (
              formData.hot_pressing_lines.map((line, idx) => (
                <tr key={idx} className={line.highlighted ? 'bg-green-100' : ''}>
                  <td className="border border-gray-400 p-1 text-center">{idx + 1}</td>
                  <td className="border border-gray-400 p-1">
                    <Input
                      value={line.description}
                      onChange={(e) => updateHotPressingLine(idx, 'description', e.target.value)}
                      className="border-0 h-8 text-sm bg-transparent"
                      placeholder="e.g., 16MM PLYWOOD - 710"
                      list="normal-pressing-descriptions"
                      data-testid={`normal-pressing-desc-${idx}`}
                    />
                  </td>
                  <td className="border border-gray-400 p-1">
                    <Input 
                      type="number"
                      value={line.ply_qty}
                      onChange={(e) => updateHotPressingLine(idx, 'ply_qty', e.target.value)}
                      className="border-0 h-8 text-sm text-center"
                    />
                  </td>
                  <td className="border border-gray-400 p-1">
                    <Input 
                      type="number"
                      value={line.side}
                      onChange={(e) => updateHotPressingLine(idx, 'side', e.target.value)}
                      className="border-0 h-8 text-sm text-center"
                    />
                  </td>
                  <td className="border border-gray-400 p-1 text-center font-bold bg-gray-50">
                    {line.total || 0}
                  </td>
                  <td className="border border-gray-400 p-1">
                    <Input 
                      value={line.details}
                      onChange={(e) => updateHotPressingLine(idx, 'details', e.target.value)}
                      className="border-0 h-8 text-sm"
                      placeholder="Details"
                    />
                  </td>
                  <td className="border border-gray-400 p-1">
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateHotPressingLine(idx, 'highlighted', !line.highlighted)}
                        className="h-6 w-6 p-0"
                        title={line.highlighted ? 'Remove highlight' : 'Highlight (e.g. added after production)'}
                        data-testid={`normal-pressing-highlight-${idx}`}
                      >
                        <Highlighter className={`w-4 h-4 ${line.highlighted ? 'text-green-600' : 'text-gray-300'}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeHotPressingLine(idx)}
                        className="h-6 w-6 p-0"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PVC & ACRYLIC PRESSING Section */}
      <div>
        <div className="bg-violet-100 p-2 font-bold text-center border-b border-gray-800 flex justify-between items-center">
          <span>PVC & ACRYLIC PRESSING DETAILS</span>
          <Button size="sm" variant="outline" onClick={addVeneerPressingLine} className="h-7" data-testid="add-pvc-pressing-row">
            <Plus className="w-4 h-4 mr-1" /> Add Row
          </Button>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-400 p-2 w-16">SL. NO.</th>
              <th className="border border-gray-400 p-2">PVC & ACRYLIC PRESSING</th>
              <th className="border border-gray-400 p-2 w-20">PLY QTY</th>
              <th className="border border-gray-400 p-2 w-16">SIDE</th>
              <th className="border border-gray-400 p-2 w-16">TOTAL</th>
              <th className="border border-gray-400 p-2">DETAILS</th>
              <th className="border border-gray-400 p-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {formData.veneer_pressing_lines.length === 0 ? (
              <tr>
                <td colSpan="7" className="border border-gray-400 p-4 text-center text-gray-500">
                  Click "Add Row" to add pressing details
                </td>
              </tr>
            ) : (
              formData.veneer_pressing_lines.map((line, idx) => (
                <tr key={idx} className={line.highlighted ? 'bg-green-100' : ''}>
                  <td className="border border-gray-400 p-1 text-center">{idx + 1}</td>
                  <td className="border border-gray-400 p-1">
                    <Input
                      value={line.description}
                      onChange={(e) => updateVeneerPressingLine(idx, 'description', e.target.value)}
                      className="border-0 h-8 text-sm bg-transparent"
                      placeholder="e.g., 19MM WPC, 17MM MULTIWOOD"
                      list="pvc-pressing-descriptions"
                      data-testid={`pvc-pressing-desc-${idx}`}
                    />
                  </td>
                  <td className="border border-gray-400 p-1">
                    <Input 
                      type="number"
                      value={line.ply_qty}
                      onChange={(e) => updateVeneerPressingLine(idx, 'ply_qty', e.target.value)}
                      className="border-0 h-8 text-sm text-center"
                    />
                  </td>
                  <td className="border border-gray-400 p-1">
                    <Input 
                      type="number"
                      value={line.side}
                      onChange={(e) => updateVeneerPressingLine(idx, 'side', e.target.value)}
                      className="border-0 h-8 text-sm text-center"
                    />
                  </td>
                  <td className="border border-gray-400 p-1 text-center font-bold bg-gray-50">
                    {line.total || 0}
                  </td>
                  <td className="border border-gray-400 p-1">
                    <Input 
                      value={line.details}
                      onChange={(e) => updateVeneerPressingLine(idx, 'details', e.target.value)}
                      className="border-0 h-8 text-sm"
                      placeholder="Details"
                    />
                  </td>
                  <td className="border border-gray-400 p-1">
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateVeneerPressingLine(idx, 'highlighted', !line.highlighted)}
                        className="h-6 w-6 p-0"
                        title={line.highlighted ? 'Remove highlight' : 'Highlight (e.g. added after production)'}
                        data-testid={`pvc-pressing-highlight-${idx}`}
                      >
                        <Highlighter className={`w-4 h-4 ${line.highlighted ? 'text-green-600' : 'text-gray-300'}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVeneerPressingLine(idx)}
                        className="h-6 w-6 p-0"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

JobCardPressingForm.displayName = 'JobCardPressingForm';

export default JobCardPressingForm;
